'use client';
import React from 'react';

import CardMetric from '@src/components/Shared/CardMetric';
import FractionDisplay from '@src/components/Shared/FractionDisplay';
import NextLink from '@src/components/Shared/NextLink';

import Bookmaker from '@src/components/Icons/Bookmaker';

import useIsMobile from '@src/hooks/useIsMobile';
import useDebounce from '@src/hooks/useDebounce';

import ForceInput from '../ForceInput/ForceInput';
import { ImpactIndexCardPropTypes } from './ImpactIndexCard.types';
import UnitDropdown from '../UnitDropdown/UnitDropdown';
import { UnitTypes } from '../UnitDropdown/UnitDropdown.types';

// IDs here must match the landingStrategy state
const units: UnitTypes[] = [
  { id: 'doubleLeg', name: 'Double Leg', unit: '' },
  { id: 'singleLeg', name: 'Single Leg', unit: '' },
];

const ImpactIndexCard: React.FC<ImpactIndexCardPropTypes> = () => {
  const isMobile = useIsMobile();

  // Main values (always up-to-date on desktop, updated on Calculate for mobile)
  const [peakForce, setPeakForce] = React.useState('');
  const [bodyWeight, setBodyWeight] = React.useState('');

  // Temporary values only on mobile
  const [tempPeakForce, setTempPeakForce] = React.useState('');
  const [tempBodyWeight, setTempBodyWeight] = React.useState('');

  const [landingStrategy, setLandingStrategy] = React.useState<'doubleLeg' | 'singleLeg'>(
    'doubleLeg'
  );

  const handleCalculate = () => {
    setPeakForce(tempPeakForce);
    setBodyWeight(tempBodyWeight);
  };

  // Which values to render
  const displayPeak = isMobile ? tempPeakForce : peakForce;
  const displayWeight = isMobile ? tempBodyWeight : bodyWeight;

  // Which values to use for calculation
  const forceN = parseFloat(peakForce) || 0;
  const weightN = parseFloat(bodyWeight) || 0;

  // (force ÷ body-weight) → BW
  const impactPercent = weightN > 0 ? forceN / weightN : 0;
  const formattedImpact = impactPercent.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Debounce the formattedImpact by 3000 ms
  const debouncedImpact = useDebounce(formattedImpact, 3000);

  const hasPendingChanges =
    isMobile && (tempPeakForce !== peakForce || tempBodyWeight !== bodyWeight);

  // Fire a tracking event whenever a valid debounced Impact Index is computed
  React.useEffect(() => {
    if (
      peakForce !== '' &&
      bodyWeight !== '' &&
      !isNaN(Number(debouncedImpact)) &&
      isFinite(Number(debouncedImpact))
    ) {
      if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'msk_calculator_calculation_performed',
          eventCategory: 'msk_calculator_usage',
          eventLabel: 'msk_calculator_impact_index',
          value: 1,
        });
      }
    }
    // Only re-run when debouncedImpact changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedImpact]);

  return (
    <CardMetric
      id='calculator-impact-index'
      title='Impact Index'
      productIcons={[
        {
          alt: 'ForceDecks App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/1sZL8SrOEPwzDxDhnJCoy2/d3561b92560bdd3fe2ecd709b6f4604f/forcedecks-app-icon.svg',
        },
      ]}
    >
      <p className='mb-[2rem] text-[1.2rem]'>
        Assess impact loads relative to body weight and functional capacity for daily activities
        (e.g. step navigation) with the Impact Index calculator.
      </p>

      {/* ———————————————— Formula ———————————————— */}
      <FractionDisplay
        className='mb-[2rem]'
        label='Impact Index'
        numerator={`<strong class="font-medium block sm:inline">${
          landingStrategy === 'singleLeg' ? 'Single Leg Land and Hold' : 'Land and Hold'
        }</strong> (Peak Drop Landing Force)`}
        denominator='<strong class="font-medium block sm:inline">Subject Mass</strong> (Body Mass)'
      />

      {/* ————— Landing Strategy Selector ————— */}
      <UnitDropdown
        className='mb-[2.4rem]'
        label='Landing Strategy:'
        units={units}
        activeUnitId={landingStrategy}
        onChange={(id) => setLandingStrategy(id as 'doubleLeg' | 'singleLeg')}
      />

      {/* ————————— Inputs ————————— */}
      <div>
        {/* Peak‐force input */}
        <ForceInput
          className='mb-[1.4rem]'
          placeholder='Newtons'
          labelLines={[
            landingStrategy === 'singleLeg' ? 'Single Leg Land and Hold' : 'Land and Hold',
            'Peak Drop Landing Force',
          ]}
          units={[{ name: 'Newtons', unit: 'N', id: 'newtons' }]}
          activeUnit='newtons'
          value={displayPeak}
          onUnitSelect={() => {}}
          onChange={(e) => {
            const v = e.target.value;
            isMobile ? setTempPeakForce(v) : setPeakForce(v);
          }}
        />

        {/* Body‐mass input */}
        <ForceInput
          className='mb-[1.4rem]'
          placeholder='Newtons'
          labelLines={['Subject Mass', 'Body Mass']}
          units={[{ name: 'Newtons', unit: 'N', id: 'newtons' }]}
          activeUnit='newtons'
          value={displayWeight}
          onUnitSelect={() => {}}
          onChange={(e) => {
            const v = e.target.value;
            isMobile ? setTempBodyWeight(v) : setBodyWeight(v);
          }}
        />
      </div>

      {/* —————— Impact Index Result —————— */}
      <div className='mb-[3.1rem] flex items-center justify-between text-primary'>
        <span className='ml-auto mr-[2rem] text-[1.4rem] font-medium'>Impact Index</span>
        <span className='border-l-[0.2rem] border-[#D9D9D9] pl-[2rem] text-[1.8rem] sm:text-[3rem]'>
          <span className='font-normal'>{formattedImpact}</span>
        </span>
      </div>

      {/* —————— Footer & Mobile Calculate —————— */}
      <div className='relative flex items-center gap-[1rem]'>
        <div className='absolute inset-x-[-9999px] top-[-2rem] h-[20rem] bg-[#F8F8F8]' />

        <div className='flex w-full items-center gap-[1.6rem] text-[1.2rem]'>
          <Bookmaker className='relative z-10 hidden h-[2rem] w-[1.6rem] max-w-[1.6rem] shrink-0 text-primary md:block' />
          <p>
            Find more information on the impact index in the VALD{' '}
            <NextLink
              className='font-light underline'
              href='https://resources.valdperformance.com/practitioners-guide-to-acl'
            >
              Practitioners Guide to ACL.
            </NextLink>
          </p>
        </div>

        {isMobile && (
          <button
            onClick={handleCalculate}
            className='common-btn-transition right-0 bottom-0 relative ml-auto inline-flex h-[3.7rem] max-h-[3.7rem] items-center justify-center rounded-[0.6rem] border-primary bg-primary px-[2rem] py-[0.8rem] text-[1.6rem] font-normal leading-[1.6rem] tracking-[-0.0304rem] text-[#fff]! hover:border-primary--v2 hover:bg-primary--v2 hover:text-primary--v2'
            disabled={!hasPendingChanges}
          >
            Calculate
          </button>
        )}
      </div>
    </CardMetric>
  );
};

export default ImpactIndexCard;
